import { NestApiType, nestCall } from '@/composables/nestApi';
import { UserState } from '@/composables/User';
import { generateSecretKey } from '@/helpers/helpers';
import { predefinedEvents } from '@/composables/events/constants';
import { SaveShopsConfiguration } from '@/composables/shop/ShopsConfiguration';
import { webhooksCreatedFromEnum, webhooksTypeEnum } from '@/types';
import type { ShopsConfigurationInput, ShopsConfigurationInputItem } from '@/types/generated-types/graphql';
import type { FlowStructureDto, FlowStepDto } from '@/types/flow-structure';

/**
 * Operator IDs for event-based triggers
 * In the flow system, event triggers use boxtriggerinternalevent with an 'event' parameter
 * that specifies the actual event type (abandon_cart, quit_site, etc.)
 * The boxwait operator also uses an event ('wait') for short delays (< MAX_BOX_WAIT_DELAY_FOR_ONSITE)
 * to enable on-site automation via webhooks.
 */
const TRIGGER_INTERNAL_EVENT_OPERATOR = 'boxtriggerinternalevent';
const WAIT_OPERATOR = 'boxwait';

/**
 * Maximum delay in seconds for boxwait to use on-site automation (webhook)
 */
const MAX_BOX_WAIT_DELAY_FOR_ONSITE = 120; // 2 minutes

/**
 * Convert time unit select to seconds
 */
function convertTimeUnitToSeconds(timeUnitSelect: { unit: string; value: number } | undefined): number {
  if (!timeUnitSelect) return 0;
  const { unit, value } = timeUnitSelect;
  switch (unit) {
    case 'MINUTE':
      return value * 60;
    case 'HOUR':
      return value * 60 * 60;
    case 'DAY':
      return value * 60 * 60 * 24;
    case 'MONTH':
      return value * 60 * 60 * 24 * 30;
    default:
      return value;
  }
}

/**
 * Get event code from step parameters
 * The event code is stored in step.parameters.event for boxtriggerinternalevent
 */
function getEventCodeFromStep(step: FlowStepDto): string | null {
  if (step.type === WAIT_OPERATOR) {
    // Wait event for short delay boxes
    return 'wait';
  }
  if (step.type !== TRIGGER_INTERNAL_EVENT_OPERATOR) {
    // Not an event trigger
    return null;
  }
  return step.parameters?.event || null;
}

/**
 * Check if a step needs a webhook (event trigger without existing webhook)
 * Only boxtriggerinternalevent and boxwait (with short delay) need webhooks
 */
function stepNeedsWebhook(step: FlowStepDto): boolean {
  const stepType = step.type;
  
  // Check if it's boxtriggerinternalevent without webhook
  // Note: parameters can be stored as strings or numbers depending on the source
  if (stepType === TRIGGER_INTERNAL_EVENT_OPERATOR) {
    const webhookId = step.parameters?.webhook;
    return !webhookId || webhookId === 0 || webhookId === '0';
  }
  
  // Check for boxwait with short delay (on-site automation)
  if (stepType === 'boxwait') {
    const webhookId = step.parameters?.webhook;
    if (webhookId && webhookId !== 0 && webhookId !== '0') {
      return false;
    }
    
    // Parse time_unit_select from parameters
    const timeUnitSelectStr = step.parameters?.time_unit_select;
    if (timeUnitSelectStr) {
      try {
        const timeUnitSelect = JSON.parse(timeUnitSelectStr);
        const delayInSeconds = convertTimeUnitToSeconds(timeUnitSelect);
        return delayInSeconds <= MAX_BOX_WAIT_DELAY_FOR_ONSITE;
      } catch {
        return false;
      }
    }
  }
  
  return false;
}

/**
 * Create events and webhooks for event trigger steps in a flow
 * Returns updated steps with webhook IDs
 */
export async function createEventsAndWebhooks(
  flow: FlowStructureDto,
  campaignName: string,
  isActive: boolean,
): Promise<{ updatedSteps: FlowStepDto[]; changed: boolean }> {
  const idShop = UserState.activeShop?.id ?? 0;
  let changed = false;
  const updatedSteps = [...flow.steps];
  
  for (let i = 0; i < updatedSteps.length; i++) {
    const step = updatedSteps[i];
    
    if (!stepNeedsWebhook(step)) {
      continue;
    }
    
    const eventCode = getEventCodeFromStep(step);
    
    // If it's a predefined event, create it in the database (idempotent)
    if (eventCode && Object.keys(predefinedEvents).includes(eventCode)) {
      const event = predefinedEvents[eventCode];
      
      await nestCall('post', NestApiType.CLIENT_API, '/events', {}, '', {
        name: event.code_name,
        code_name: event.code_name,
        type: event.type,
        properties: event.properties,
      });
    }
    
    // Create the webhook
    const secretKey = generateSecretKey(50);
    const keyUrl = btoa(`${idShop}-${secretKey}`);
    
    const webhookResponse = await nestCall('post', NestApiType.CLIENT_API, '/webhooks', {}, '', {
      name: `Webhook ${campaignName} - ${step.id}`,
      created_from: webhooksCreatedFromEnum.UI,
      type: webhooksTypeEnum.INTERNAL,
      url: `/trigger/${keyUrl}`,
      secret_key: secretKey,
      events: eventCode ? [eventCode] : [],
      active: isActive,
    });
    
    if (webhookResponse && webhookResponse.success && webhookResponse.id) {
      // Update the step with the webhook ID
      updatedSteps[i] = {
        ...step,
        parameters: {
          ...step.parameters,
          webhook: Number(webhookResponse.id),
        },
      };
      changed = true;
    }
  }
  
  return { updatedSteps, changed };
}

/**
 * Create shops_configuration entries for periodic trigger steps
 */
export async function createPeriodicTriggerConfigurations(
  flow: FlowStructureDto,
): Promise<void> {
  const idShop = UserState.activeShop?.id ?? 0;
  const createdKeys: string[] = [];
  
  // Find all periodic launching steps with a time configured
  for (const step of flow.steps) {
    if (step.type === 'boxperiodiclaunching') {
      const time = step.parameters?.time;
      
      if (time && time !== '') {
        const formattedTime = time.replace(':', '_');
        
        if (!createdKeys.includes(formattedTime)) {
          createdKeys.push(formattedTime);
        }
      }
    }
  }
  
  if (createdKeys.length > 0) {
    const input: ShopsConfigurationInput = {
      id_shop: idShop,
      configs: createdKeys.map((time): ShopsConfigurationInputItem => ({
        key: `use_periodic_trigger_${time}`,
        value: '1',
        lang: '',
      })),
    };
    
    await SaveShopsConfiguration(input);
  }
}

/**
 * Process all trigger-related operations before saving a flow
 * This is the main entry point to be called from FlowEditorModal
 */
export async function processFlowTriggersBeforeSave(
  flow: FlowStructureDto,
  campaignName: string,
  isActive: boolean,
): Promise<FlowStructureDto> {
  // Create events and webhooks for event triggers
  const { updatedSteps, changed } = await createEventsAndWebhooks(flow, campaignName, isActive);
  
  // Create periodic trigger configurations
  await createPeriodicTriggerConfigurations(flow);
  
  // Return updated flow if steps were modified
  if (changed) {
    return {
      ...flow,
      steps: updatedSteps,
    };
  }
  
  return flow;
}
